import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Reveal from '../../../shared/Reveal/Reveal';
import { submitAccountApplication } from '../../../../api/accountSubmissionsApi';
import { countryOptions, phoneCodeOptions } from '../../../../data/countries';
import { validateCreateAccountForm } from '../../../../utils/validateCreateAccountForm';
import uploadIcon from '../../../../assets/images/technologynav/CloudArrowUp.svg';
import Select from './Select';
import './Form.css';

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'application/pdf',
];

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ACCOUNT_TYPES = [
  { id: 'cfd', label: 'CFD' },
  { id: 'investment', label: 'Investment' },
  { id: 'saving', label: 'Saving' },
];

const HEADERS = {
  cfd: {
    title: 'Open a CFD Account',
    text: 'Trade global markets with institutional execution. Complete the form and our team will prepare your account.',
  },
  investment: {
    title: 'Open an Investment Account',
    text: 'Invest in listed companies and products through VBS. Share a few details so we can set up your account.',
  },
  saving: {
    title: 'Open a Saving Account',
    text: 'Build reserves with VBS Wealth & Savings. Tell us about your goals and we will match the right account.',
  },
};

const YES_NO = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
];

const AMOUNTS = ['Below 5k', '5k – 25k', '25k – 100k', 'Above 100k'].map((amount) => ({
  value: amount,
  label: amount,
}));

const EXPERIENCE = ['0–1 years', '1–3 years', '3–5 years', '5+ years'].map((item) => ({
  value: item,
  label: item,
}));

const CFD_ACCOUNT_TYPES = [
  { value: 'Standard Pro', label: 'Standard Pro' },
  { value: 'Standard', label: 'Standard' },
  { value: 'VIP', label: 'VIP' },
];

const LEVERAGE_OPTIONS = [
  { value: '1:50', label: '1:50' },
  { value: '1:100', label: '1:100' },
  { value: '1:200', label: '1:200' },
  { value: '1:500', label: '1:500' },
];

const INVESTMENT_TYPES = [
  { value: 'Stocks', label: 'Stocks' },
  { value: 'ETFs', label: 'ETFs' },
  { value: 'Indices', label: 'Indices' },
  { value: 'Forex', label: 'Forex' },
  { value: 'Commodities', label: 'Commodities' },
];

const PRODUCTS = [
  { value: 'Amazon', label: 'Amazon' },
  { value: 'Apple', label: 'Apple' },
  { value: 'Microsoft', label: 'Microsoft' },
  { value: 'Alphabet', label: 'Alphabet' },
  { value: 'Tesla', label: 'Tesla' },
  { value: 'NVIDIA', label: 'NVIDIA' },
];

const SAVING_ACCOUNT_TYPES = [
  { value: 'ISA / Structured Savings', label: 'ISA / Structured Savings' },
  { value: 'Active Saving Account', label: 'Active Saving Account' },
  { value: 'Interest Account', label: 'Interest Account' },
  { value: 'Structured Savings', label: 'Structured Savings' },
];

const FieldError = ({ message }) =>
  message ? <p className="create-account__field-error">{message}</p> : null;

const buildFormData = (values) => {
  const formData = new FormData();

  formData.append('accountKind', values.accountKind);
  formData.append('firstName', values.firstName.trim());
  formData.append('lastName', values.lastName.trim());
  formData.append('email', values.email.trim());
  formData.append('countryCode', values.countryCode.trim());
  formData.append('phone', values.phone.trim());
  formData.append('address', values.address.trim());
  formData.append('city', values.city.trim());
  formData.append('region', values.region.trim());
  formData.append('postalCode', values.postalCode.trim());
  formData.append('country', values.country.trim());
  formData.append('amount', values.amount);
  formData.append('experience', values.experience);
  formData.append('priorExperience', values.priorExperience);
  formData.append('consultation', values.consultation);
  formData.append('agreedToPrivacy', values.agreed ? 'true' : 'false');
  formData.append('passport', values.passportFile);

  if (values.accountKind === 'cfd') {
    formData.append('cfdAccountType', values.cfdAccountType);
    formData.append('leverage', values.leverage);
  }

  if (values.accountKind === 'investment') {
    formData.append('investmentType', values.investmentType);
    formData.append('selectedProduct', values.selectedProduct);
  }

  if (values.accountKind === 'saving') {
    formData.append('savingAccountType', values.savingAccountType);
  }

  return formData;
};

const CreateAccountForm = () => {
  const [accountKind, setAccountKind] = useState('cfd');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [passportFile, setPassportFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [uploadError, setUploadError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [countryCode, setCountryCode] = useState('US');
  const [country, setCountry] = useState('');
  const [cfdAccountType, setCfdAccountType] = useState('Standard Pro');
  const [leverage, setLeverage] = useState('1:500');
  const [investmentType, setInvestmentType] = useState('Stocks');
  const [selectedProduct, setSelectedProduct] = useState('Amazon');
  const [savingAccountType, setSavingAccountType] = useState('ISA / Structured Savings');
  const [amount, setAmount] = useState('Below 5k');
  const [experience, setExperience] = useState('');
  const [priorExperience, setPriorExperience] = useState('');
  const [consultation, setConsultation] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const inputClass = (field) =>
    `create-account__input${fieldErrors[field] ? ' create-account__input--error' : ''}`;

  const clearFieldError = (field) => {
    setFieldErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const getFormValues = () => ({
    accountKind,
    firstName,
    lastName,
    email,
    countryCode,
    phone,
    address,
    city,
    region,
    postalCode,
    country,
    passportFile,
    cfdAccountType,
    leverage,
    investmentType,
    selectedProduct,
    savingAccountType,
    amount,
    experience,
    priorExperience,
    consultation,
    agreed,
  });

  const resetUpload = () => {
    setPassportFile(null);
    setUploadProgress(0);
    setUploadStatus('idle');
    setUploadError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const acceptFile = (file) => {
    if (!file) {
      return;
    }

    const isAcceptedType =
      ACCEPTED_TYPES.includes(file.type) || /\.(png|jpe?g|pdf)$/i.test(file.name);

    if (!isAcceptedType) {
      setUploadError('Please upload a PNG, JPG, or PDF file.');
      setUploadStatus('error');
      setPassportFile(null);
      return;
    }

    if (file.size > MAX_FILE_BYTES) {
      setUploadError('File is too large. Maximum size is 5 MB.');
      setUploadStatus('error');
      setPassportFile(null);
      return;
    }

    setPassportFile(file);
    setUploadError('');
    setUploadProgress(0);
    setUploadStatus('selected');
    clearFieldError('passport');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');

    const values = getFormValues();
    const validation = validateCreateAccountForm(values);

    if (!validation.isValid) {
      setFieldErrors(validation.errors);
      setFormError('Please fix the highlighted fields and try again.');
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);
    setUploadProgress(0);
    setUploadStatus('uploading');

    try {
      await submitAccountApplication(buildFormData(values), (progressEvent) => {
        if (!progressEvent.total) {
          return;
        }

        const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
        setUploadProgress(percent);
      });

      setUploadStatus('done');
      setIsSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setUploadStatus(passportFile ? 'selected' : 'idle');
      setUploadProgress(0);

      if (error.errors?.length) {
        setFormError(error.errors.join(' '));
      } else {
        setFormError(error.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <section className="create-account">
        <div className="create-account__inner">
          <Reveal className="create-account__intro">
            <h1 className="create-account__title">Application received</h1>
            <p className="create-account__support">
              Thank you for your application. Our team will review your details and contact you
              shortly.
            </p>
          </Reveal>
        </div>
      </section>
    );
  }

  return (
    <section className="create-account">
      <div className="create-account__inner">
        <Reveal className="create-account__intro">
          <div className="create-account__intro-stack">
            {ACCOUNT_TYPES.map(({ id }) => (
              <div
                key={id}
                className={`create-account__intro-variant${
                  accountKind === id ? ' create-account__intro-variant--active' : ''
                }`}
                aria-hidden={accountKind !== id}
              >
                <h1 className="create-account__title">{HEADERS[id].title}</h1>
                <p className="create-account__support">{HEADERS[id].text}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal className="create-account__panel" delay={80}>
          <form className="create-account__form" onSubmit={handleSubmit} noValidate>
            {formError ? (
              <p className="create-account__form-error" role="alert">
                {formError}
              </p>
            ) : null}

            <div className="create-account__field">
              <p className="create-account__label">Choose account type</p>
              <div className="create-account__tabs" role="tablist">
                {ACCOUNT_TYPES.map(({ id, label }) => (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={accountKind === id}
                    className={`create-account__tab ${
                      accountKind === id ? 'create-account__tab--active' : ''
                    }`}
                    onClick={() => {
                      setAccountKind(id);
                      clearFieldError('accountKind');
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <FieldError message={fieldErrors.accountKind} />
            </div>

            <div className="create-account__row">
              <label className="create-account__field">
                <span className="create-account__label">First name</span>
                <input
                  className={inputClass('firstName')}
                  type="text"
                  name="firstName"
                  value={firstName}
                  onChange={(event) => {
                    setFirstName(event.target.value);
                    clearFieldError('firstName');
                  }}
                  placeholder="First name"
                  autoComplete="given-name"
                />
                <FieldError message={fieldErrors.firstName} />
              </label>
              <label className="create-account__field">
                <span className="create-account__label">Last name</span>
                <input
                  className={inputClass('lastName')}
                  type="text"
                  name="lastName"
                  value={lastName}
                  onChange={(event) => {
                    setLastName(event.target.value);
                    clearFieldError('lastName');
                  }}
                  placeholder="Last name"
                  autoComplete="family-name"
                />
                <FieldError message={fieldErrors.lastName} />
              </label>
            </div>

            <div className="create-account__row">
              <label className="create-account__field">
                <span className="create-account__label">Email</span>
                <input
                  className={inputClass('email')}
                  type="email"
                  name="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    clearFieldError('email');
                  }}
                  placeholder="you@example.com"
                  autoComplete="email"
                />
                <FieldError message={fieldErrors.email} />
              </label>
              <div className="create-account__field">
                <span className="create-account__label">Phone number</span>
                <div className="create-account__phone">
                  <Select
                    className="create-account__dropdown--phone"
                    name="countryCode"
                    options={phoneCodeOptions}
                    value={countryCode}
                    onChange={(value) => {
                      setCountryCode(value);
                      clearFieldError('countryCode');
                    }}
                    searchable
                    searchPlaceholder="Search country or code"
                    aria-label="Country code"
                  />
                  <input
                    className={`${inputClass('phone')} create-account__input--phone`}
                    type="tel"
                    name="phone"
                    value={phone}
                    onChange={(event) => {
                      setPhone(event.target.value);
                      clearFieldError('phone');
                    }}
                    placeholder="+1 (555) 000-0000"
                    autoComplete="tel"
                  />
                </div>
                <FieldError message={fieldErrors.countryCode || fieldErrors.phone} />
              </div>
            </div>

            <label className="create-account__field">
              <span className="create-account__label">Address</span>
              <input
                className={inputClass('address')}
                type="text"
                name="address"
                value={address}
                onChange={(event) => {
                  setAddress(event.target.value);
                  clearFieldError('address');
                }}
                placeholder="Address"
                autoComplete="street-address"
              />
              <FieldError message={fieldErrors.address} />
            </label>

            <div className="create-account__row">
              <label className="create-account__field">
                <span className="create-account__label">City</span>
                <input
                  className={inputClass('city')}
                  type="text"
                  name="city"
                  value={city}
                  onChange={(event) => {
                    setCity(event.target.value);
                    clearFieldError('city');
                  }}
                  placeholder="City"
                  autoComplete="address-level2"
                />
                <FieldError message={fieldErrors.city} />
              </label>
              <label className="create-account__field">
                <span className="create-account__label">State/Province/Region</span>
                <input
                  className={inputClass('region')}
                  type="text"
                  name="region"
                  value={region}
                  onChange={(event) => {
                    setRegion(event.target.value);
                    clearFieldError('region');
                  }}
                  placeholder="State/Province/Region"
                  autoComplete="address-level1"
                />
                <FieldError message={fieldErrors.region} />
              </label>
            </div>

            <div className="create-account__row">
              <label className="create-account__field">
                <span className="create-account__label">ZIP/Postal</span>
                <input
                  className={inputClass('postalCode')}
                  type="text"
                  name="postalCode"
                  value={postalCode}
                  onChange={(event) => {
                    setPostalCode(event.target.value);
                    clearFieldError('postalCode');
                  }}
                  placeholder="ZIP/Postal"
                  autoComplete="postal-code"
                />
                <FieldError message={fieldErrors.postalCode} />
              </label>
              <div className="create-account__field">
                <span className="create-account__label">Select country</span>
                <Select
                  name="country"
                  options={countryOptions}
                  value={country}
                  onChange={(value) => {
                    setCountry(value);
                    clearFieldError('country');
                  }}
                  placeholder="Select country"
                  searchable
                  searchPlaceholder="Search country"
                />
                <FieldError message={fieldErrors.country} />
              </div>
            </div>

            <div className="create-account__field">
              <span className="create-account__label">Attach passport</span>

              {uploadStatus === 'idle' || uploadStatus === 'error' ? (
                <button
                  type="button"
                  className={`create-account__upload ${
                    isDragging ? 'create-account__upload--active' : ''
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setIsDragging(false);
                    acceptFile(event.dataTransfer.files?.[0]);
                  }}
                >
                  <span className="create-account__upload-icon">
                    <img src={uploadIcon} alt="" aria-hidden="true" />
                  </span>
                  <span className="create-account__upload-title">
                    <strong>Click to upload</strong> or drag and drop
                  </span>
                  <span className="create-account__upload-hint">
                    PNG, JPG or PDF (max. 5mb)
                  </span>
                </button>
              ) : (
                <div
                  className={`create-account__upload-progress ${
                    uploadStatus === 'done' || uploadStatus === 'selected'
                      ? 'create-account__upload-progress--done'
                      : ''
                  }`}
                  role="status"
                  aria-live="polite"
                >
                  <div className="create-account__upload-progress-top">
                    <div className="create-account__upload-progress-meta">
                      <span className="create-account__upload-progress-name">
                        {passportFile?.name}
                      </span>
                      <span className="create-account__upload-progress-size">
                        {passportFile ? formatFileSize(passportFile.size) : ''}
                      </span>
                    </div>
                    <span className="create-account__upload-progress-percent">
                      {uploadStatus === 'uploading'
                        ? `${uploadProgress}%`
                        : uploadStatus === 'done'
                          ? 'Submitted'
                          : 'Ready'}
                    </span>
                  </div>

                  {uploadStatus === 'uploading' ? (
                    <div
                      className="create-account__upload-progress-track"
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={uploadProgress}
                      aria-label="Upload progress"
                    >
                      <div
                        className="create-account__upload-progress-fill"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  ) : null}

                  <div className="create-account__upload-progress-actions">
                    {uploadStatus === 'uploading' ? (
                      <span className="create-account__upload-progress-status">
                        Submitting application…
                      </span>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="create-account__upload-progress-link"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isSubmitting}
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          className="create-account__upload-progress-link"
                          onClick={resetUpload}
                          disabled={isSubmitting}
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {uploadError ? <p className="create-account__upload-error">{uploadError}</p> : null}
              <FieldError message={fieldErrors.passport} />

              <input
                ref={fileInputRef}
                className="create-account__file"
                type="file"
                name="passport"
                accept=".png,.jpg,.jpeg,.pdf"
                onChange={(event) => acceptFile(event.target.files?.[0])}
              />
            </div>

            <div className="create-account__type-fields">
              <div
                className={`create-account__type-variant${
                  accountKind === 'cfd' ? ' create-account__type-variant--active' : ''
                }`}
                aria-hidden={accountKind !== 'cfd'}
              >
                <div className="create-account__row">
                  <div className="create-account__field">
                    <span className="create-account__label">Account type</span>
                    <Select
                      name="cfdAccountType"
                      options={CFD_ACCOUNT_TYPES}
                      value={cfdAccountType}
                      onChange={(value) => {
                        setCfdAccountType(value);
                        clearFieldError('cfdAccountType');
                      }}
                      disabled={accountKind !== 'cfd'}
                    />
                    <FieldError message={fieldErrors.cfdAccountType} />
                  </div>
                  <div className="create-account__field">
                    <span className="create-account__label">Leverage</span>
                    <Select
                      name="leverage"
                      options={LEVERAGE_OPTIONS}
                      value={leverage}
                      onChange={(value) => {
                        setLeverage(value);
                        clearFieldError('leverage');
                      }}
                      disabled={accountKind !== 'cfd'}
                    />
                    <FieldError message={fieldErrors.leverage} />
                  </div>
                </div>
              </div>

              <div
                className={`create-account__type-variant${
                  accountKind === 'investment' ? ' create-account__type-variant--active' : ''
                }`}
                aria-hidden={accountKind !== 'investment'}
              >
                <div className="create-account__row">
                  <div className="create-account__field">
                    <span className="create-account__label">Investment type</span>
                    <Select
                      name="investmentType"
                      options={INVESTMENT_TYPES}
                      value={investmentType}
                      onChange={(value) => {
                        setInvestmentType(value);
                        clearFieldError('investmentType');
                      }}
                      disabled={accountKind !== 'investment'}
                    />
                    <FieldError message={fieldErrors.investmentType} />
                  </div>
                  <div className="create-account__field">
                    <span className="create-account__label">Selected Product</span>
                    <Select
                      name="selectedProduct"
                      options={PRODUCTS}
                      value={selectedProduct}
                      onChange={(value) => {
                        setSelectedProduct(value);
                        clearFieldError('selectedProduct');
                      }}
                      disabled={accountKind !== 'investment'}
                    />
                    <FieldError message={fieldErrors.selectedProduct} />
                  </div>
                </div>
              </div>

              <div
                className={`create-account__type-variant${
                  accountKind === 'saving' ? ' create-account__type-variant--active' : ''
                }`}
                aria-hidden={accountKind !== 'saving'}
              >
                <div className="create-account__field">
                  <span className="create-account__label">Account type</span>
                  <Select
                    name="savingAccountType"
                    options={SAVING_ACCOUNT_TYPES}
                    value={savingAccountType}
                    onChange={(value) => {
                      setSavingAccountType(value);
                      clearFieldError('savingAccountType');
                    }}
                    disabled={accountKind !== 'saving'}
                  />
                  <FieldError message={fieldErrors.savingAccountType} />
                </div>
              </div>
            </div>

            <div className="create-account__row">
              <div className="create-account__field">
                <span className="create-account__label create-account__label--fixed">
                  What amount you planning to invest?
                </span>
                <Select
                  name="amount"
                  options={AMOUNTS}
                  value={amount}
                  onChange={(value) => {
                    setAmount(value);
                    clearFieldError('amount');
                  }}
                />
                <FieldError message={fieldErrors.amount} />
              </div>
              <div className="create-account__field">
                <span className="create-account__label create-account__label--fixed">
                  Years of Experience
                </span>
                <Select
                  name="experience"
                  options={EXPERIENCE}
                  value={experience}
                  onChange={(value) => {
                    setExperience(value);
                    clearFieldError('experience');
                  }}
                  placeholder="Select"
                />
                <FieldError message={fieldErrors.experience} />
              </div>
            </div>

            <div className="create-account__row">
              <div className="create-account__field">
                <span className="create-account__label create-account__label--fixed">
                  {accountKind === 'cfd' ? 'Have you traded before?' : 'Have you invested before?'}
                </span>
                <Select
                  name="priorExperience"
                  options={YES_NO}
                  value={priorExperience}
                  onChange={(value) => {
                    setPriorExperience(value);
                    clearFieldError('priorExperience');
                  }}
                  placeholder="Select"
                />
                <FieldError message={fieldErrors.priorExperience} />
              </div>
              <div className="create-account__field">
                <span className="create-account__label create-account__label--fixed">
                  Are you interested in live consultation?
                </span>
                <Select
                  name="consultation"
                  options={YES_NO}
                  value={consultation}
                  onChange={(value) => {
                    setConsultation(value);
                    clearFieldError('consultation');
                  }}
                  placeholder="Select"
                />
                <FieldError message={fieldErrors.consultation} />
              </div>
            </div>

            <label className="create-account__consent">
              <input
                className="create-account__checkbox"
                type="checkbox"
                name="privacyPolicy"
                checked={agreed}
                onChange={(event) => {
                  setAgreed(event.target.checked);
                  clearFieldError('agreed');
                }}
              />
              <span>
                You agree to our friendly{' '}
                <Link
                  to="/privacy"
                  className="create-account__consent-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  privacy policy
                </Link>
                .
              </span>
            </label>
            <FieldError message={fieldErrors.agreed} />

            <button
              type="submit"
              className="create-account__submit luxury-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting…' : 'Get started'}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default CreateAccountForm;
